import * as React from 'react';
import { Box, Grid } from '@mui/material';
import {
	CerebroComplexListItem,
	CerebroDrawer,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroStyledBox,
	CerebroTextarea,
	DebtCapitalHeading,
} from '.';
import { ReactComponent as OtherFinancingNeeds } from '../icons/OtherFinancingNeeds.svg';
import { DealRequestAllocation } from '../models';
import { Field, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../context';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { getApiResponseData } from '../utils/axios';
import { DebtCapitalSectionProps } from '../../pages';
import { CerebroApiEndpoints } from '../utils/api';
import { QueryKeys } from '../hooks';
import { HtmlUtils, NumberUtils } from '../utils';
import { CerebroForm } from './CerebroForm';
import { MaxCurrencyLength } from '../utils/platform';

export const CerebroDebtOtherFinancingNeeds: React.FC<DebtCapitalSectionProps> = ({
	allocation,
	drawerOpenByDefault = false,
}) => {
	const [open, setOpen] = React.useState(drawerOpenByDefault);
	const api = useApi();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();

	const isEmpty = isOtherFinancingNeedsEmpty(allocation);

	const { mutate } = useMutation(
		async (values: DealRequestAllocationOtherValues) => {
			const payload: Partial<DealRequestAllocation> = {
				otherFinancingAmount: NumberUtils.ParseDecimal(values.otherFinancingAmount),
				otherFinancingDescription: values.otherFinancingDescription,
			};

			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), payload)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				setOpen(false);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	const drawerRef = React.useRef<HTMLDivElement>(null);

	return (
		<>
			<DebtCapitalHeading icon={OtherFinancingNeeds} amountRequested={allocation.otherFinancingAmount ?? 0}>
				Other Financing Needs
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
					<br />
					<CerebroPrimaryButton wide onClick={() => setOpen(true)}>
						Add Other Financing Needs Details
					</CerebroPrimaryButton>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }} onEdit={() => setOpen(true)}>
					<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
						Description of additional needs
					</CerebroLabel>
					<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
						{allocation.otherFinancingDescription}
					</CerebroText>
				</CerebroComplexListItem>
			)}

			<CerebroDrawer ref={drawerRef} open={open} onClose={() => setOpen(false)} icon={OtherFinancingNeeds}>
				<CerebroHeading>Other Financing Needs</CerebroHeading>
				<br />
				<Formik
					initialValues={{
						otherFinancingAmount: NumberUtils.FormatCurrency(allocation.otherFinancingAmount),
						otherFinancingDescription: allocation.otherFinancingDescription,
					}}
					validationSchema={ValidationSchema}
					onSubmit={values => mutate(values)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values }) => (
						<CerebroForm
							flatIndicator
							onSubmit={handleSubmit}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
						>
							<Box sx={{ marginBottom: 2 }}>
								<CerebroLabel
									required
									htmlFor="otherFinancingAmount"
									error={touched.otherFinancingAmount && errors.otherFinancingAmount != null}
								>
									Amount
								</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="otherFinancingAmount"
									name="otherFinancingAmount"
									disabled={isSubmitting}
									value={values.otherFinancingAmount}
									error={touched.otherFinancingAmount && errors.otherFinancingAmount != null}
								/>
								{touched.otherFinancingAmount && errors.otherFinancingAmount != null && (
									<CerebroText variant="error">{errors.otherFinancingAmount}</CerebroText>
								)}
							</Box>

							<Box sx={{ marginBottom: 2 }}>
								<CerebroLabel
									required
									htmlFor="otherFinancingDescription"
									error={
										touched.otherFinancingDescription && errors.otherFinancingDescription != null
									}
								>
									Description
								</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="otherFinancingDescription"
									name="otherFinancingDescription"
									disabled={isSubmitting}
									rows={4}
									value={values.otherFinancingDescription}
									error={
										touched.otherFinancingDescription && errors.otherFinancingDescription != null
									}
								/>
								{touched.otherFinancingDescription && errors.otherFinancingDescription != null && (
									<CerebroText variant="error">{errors.otherFinancingDescription}</CerebroText>
								)}
							</Box>

							<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
								<Grid item>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save Details
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					)}
				</Formik>
			</CerebroDrawer>
		</>
	);
};

interface DealRequestAllocationOtherValues {
	otherFinancingAmount?: string;
	otherFinancingDescription?: string;
}

const ValidationSchema = Yup.object().shape({
	otherFinancingAmount: Yup.string().required('Amount is required!'),
	otherFinancingDescription: Yup.string().required('Description is required!'),
});

export function isOtherFinancingNeedsEmpty(allocation: DealRequestAllocation): boolean {
	return !allocation.otherFinancingAmount;
}
